import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CategoryDTO } from '@em/shared';
import { api, qk, queryClient, useCategories, useMyobStatus } from '../../api';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, EmptyState, ErrorAlert, Input, QueryState, Select } from '../../components/ui';

export function CategoriesSettings() {
  const q = useCategories();
  const myob = useMyobStatus();
  const connected = !!myob.data?.connected && !!myob.data.companyFileUri;
  const remote = useQuery({ queryKey: qk.myobCategories, queryFn: api.myobCategories, enabled: connected, retry: false });
  const [adding, setAdding] = useState(false);

  return (
    <Card
      title="Payroll categories"
      tight
      actions={
        <Button size="sm" onClick={() => setAdding(true)} disabled={adding}>
          Add category
        </Button>
      }
    >
      <div className="card-body stack tight">
        <p className="small muted" style={{ margin: 0 }}>
          The engine emits these codes; MYOB applies the pay rates. For the import file, the MYOB name must match the payroll category name in AccountRight exactly. For the API, pick the category so its UID is stored.
        </p>
        {connected ? (
          remote.isError ? (
            <ErrorAlert error={remote.error} title="Could not load MYOB payroll categories" />
          ) : (
            <Alert tone="green">MYOB is connected: choose from the company file's payroll categories.</Alert>
          )
        ) : (
          <Alert tone="accent">MYOB is not connected, so type the MYOB names by hand.</Alert>
        )}
      </div>
      <QueryState q={q}>
        {(list) =>
          list.length === 0 && !adding ? (
            <EmptyState title="No categories" />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Unit</th>
                    <th>MYOB payroll category</th>
                    <th>MYOB UID</th>
                    <th title="Paid outside MYOB: left out of the export and listed on the Cash hours report">Paid in cash</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {adding && <CategoryRow isNew remote={remote.data} onDone={() => setAdding(false)} />}
                  {list.map((c) => (
                    <CategoryRow key={c.code} cat={c} remote={remote.data} />
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </QueryState>
    </Card>
  );
}

function CategoryRow({ cat, isNew, remote, onDone }: { cat?: CategoryDTO; isNew?: boolean; remote?: { uid: string; name: string; type: string | null }[]; onDone?: () => void }) {
  const toast = useToast();
  const [code, setCode] = useState(cat?.code ?? '');
  const [name, setName] = useState(cat?.name ?? '');
  const [unit, setUnit] = useState<'hours' | 'units'>(cat?.unit ?? 'hours');
  const [myobName, setMyobName] = useState(cat?.myobName ?? '');
  const [myobUid, setMyobUid] = useState(cat?.myobUid ?? '');
  const [paidInCash, setPaidInCash] = useState(cat?.paidInCash ?? false);
  const [busy, setBusy] = useState(false);
  const dirty = isNew || name !== cat?.name || unit !== cat?.unit || (myobName || null) !== cat?.myobName || (myobUid || null) !== cat?.myobUid || paidInCash !== cat?.paidInCash;

  const save = async () => {
    setBusy(true);
    try {
      await api.upsertCategory(code.trim().toUpperCase(), { name: name.trim(), unit, myobName: myobName.trim() || null, myobUid: myobUid.trim() || null, paidInCash });
      await queryClient.invalidateQueries({ queryKey: qk.categories });
      void queryClient.invalidateQueries({ queryKey: ['export'] });
      toast.success(`Category ${code.toUpperCase()} saved`);
      onDone?.();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td className="strong">
        {isNew ? <Input className="sm" aria-label="Code" placeholder="e.g. ALLOW_FIRST_AID" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} style={{ width: 150 }} /> : <span className="mono">{code}</span>}
      </td>
      <td>
        <Input className="sm" aria-label={`Name for ${code || 'new category'}`} value={name} onChange={(e) => setName(e.target.value)} />
      </td>
      <td>
        <Select className="sm" aria-label="Unit" value={unit} onChange={(e) => setUnit(e.target.value as 'hours' | 'units')} style={{ width: 90 }}>
          <option value="hours">Hours</option>
          <option value="units">Units</option>
        </Select>
      </td>
      <td>
        {remote ? (
          <Select
            className="sm"
            aria-label={`MYOB category for ${code}`}
            value={myobUid}
            onChange={(e) => {
              const r = remote.find((x) => x.uid === e.target.value);
              setMyobUid(r?.uid ?? '');
              setMyobName(r?.name ?? '');
            }}
          >
            <option value="">Not mapped</option>
            {myobUid && !remote.some((r) => r.uid === myobUid) && <option value={myobUid}>{myobName || myobUid} (not in MYOB)</option>}
            {remote.map((r) => (
              <option key={r.uid} value={r.uid}>
                {r.name}
                {r.type ? ` (${r.type})` : ''}
              </option>
            ))}
          </Select>
        ) : (
          <Input className="sm" aria-label={`MYOB name for ${code}`} value={myobName} onChange={(e) => setMyobName(e.target.value)} placeholder="Name in AccountRight" />
        )}
        {paidInCash ? <Badge tone="grey">Not exported</Badge> : !myobName && !isNew && <Badge tone="amber">Unmapped</Badge>}
      </td>
      <td>
        <Input className="sm mono" aria-label={`MYOB UID for ${code}`} value={myobUid} onChange={(e) => setMyobUid(e.target.value)} disabled={!!remote} />
      </td>
      <td>
        <label className="row" style={{ gap: 6 }}>
          <input type="checkbox" checked={paidInCash} onChange={(e) => setPaidInCash(e.target.checked)} aria-label={`${code || 'New category'} is paid in cash`} />
          <span className="small">Cash</span>
        </label>
      </td>
      <td className="actions">
        {isNew && (
          <Button size="sm" onClick={onDone}>
            Cancel
          </Button>
        )}
        <Button size="sm" variant="primary" loading={busy} disabled={!dirty || !code.trim() || !name.trim()} onClick={save}>
          Save
        </Button>
      </td>
    </tr>
  );
}
